import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  InlineGrid,
  Box,
  Button,
  Spinner,
  Banner,
  Badge,
  DataTable,
  Divider,
} from '@shopify/polaris';
import { BulkQuantityModal } from '../components/inquiry/BulkQuantityModal';
import { useInquiryStore } from '../store/inquiryStore';
import { useShortlistStore } from '../store/shortlistStore';
import { useUIStore } from '../store/uiStore';

interface ProductImage {
  id: string;
  imageUrl: string;
  isPrimary: boolean;
  colorVariant: string | null;
}

interface ProductPricing {
  id: string;
  quantityTier: string;
  unitPrice: number;
}

interface Product {
  id: string;
  name: string;
  slug: string;
  sku: string;
  description: string | null;
  samplePrice: number | null;
  isHotSelling: boolean;
  status: string;
  category: {
    id: string;
    name: string;
    slug: string;
  };
  images: ProductImage[];
  pricing: ProductPricing[];
}

interface RelatedProduct {
  id: string;
  name: string;
  slug: string;
  primaryImage: string | null;
  startingPrice: number | null;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export function ProductPage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<RelatedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  // Store hooks
  const { addItem } = useInquiryStore();
  const { addItem: addToShortlist, removeItem: removeFromShortlist, isInShortlist } = useShortlistStore();
  const { showToast } = useUIStore();

  // Handle Request Quote - Open bulk quantity modal
  const handleRequestQuote = useCallback(() => {
    if (!product) return;
    setBulkModalOpen(true);
  }, [product]);

  // Handle Request Sample - Silently add sample to inquiry basket
  const handleRequestSample = useCallback(() => {
    if (!product) return;

    addItem({
      productId: product.id,
      productName: product.name,
      productSlug: product.slug,
      productImage: product.images[0]?.imageUrl || null,
      quantity: 1,
      color: null,
      type: 'SAMPLE',
    });

    showToast('Sample added to inquiry basket!', 'success');
  }, [product, addItem, showToast]);

  // Handle Shortlist toggle
  const handleToggleShortlist = useCallback(() => {
    if (!product) return;

    if (isInShortlist(product.id)) {
      removeFromShortlist(product.id);
      showToast('Removed from shortlist', 'info');
    } else {
      addToShortlist({
        productId: product.id,
        name: product.name,
        slug: product.slug,
        image: product.images[0]?.imageUrl || null,
      });
      showToast('Added to shortlist', 'success');
    }
  }, [product, isInShortlist, addToShortlist, removeFromShortlist, showToast]);

  // Memoized function to set primary image from product data
  const setPrimaryImage = useCallback((productData: Product) => {
    if (productData.images?.length > 0) {
      const primary = productData.images.find((img) => img.isPrimary);
      setSelectedImage(primary?.imageUrl || productData.images[0].imageUrl);
    }
  }, []);

  useEffect(() => {
    // AbortController for cleanup on unmount or slug change
    const controller = new AbortController();
    const { signal } = controller;

    async function fetchProduct() {
      if (!slug) return;

      setLoading(true);
      setError(null);

      try {
        // Single efficient fetch using the by-slug endpoint
        const response = await fetch(`${API_URL}/products/by-slug/${slug}`, { signal });

        if (!response.ok) {
          if (response.status === 404) {
            setError('Product not found');
          } else {
            throw new Error('Failed to fetch product');
          }
          return;
        }

        const data = await response.json();
        const productData = data.data as Product;

        setProduct(productData);
        setPrimaryImage(productData);

        // Fetch related products (non-blocking)
        try {
          const relatedRes = await fetch(
            `${API_URL}/products/${productData.id}/related?limit=4`,
            { signal }
          );
          if (relatedRes.ok) {
            const relatedData = await relatedRes.json();
            setRelatedProducts(relatedData.data || []);
          }
        } catch {
          // Related products are optional, don't fail the page
        }
      } catch (err) {
        // Ignore abort errors (component unmounted)
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        setError('Failed to load product.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchProduct();

    // Cleanup: abort fetch on unmount or slug change
    return () => {
      controller.abort();
    };
  }, [slug, setPrimaryImage]);

  if (loading) {
    return (
      <Page title="Loading...">
        <Box padding="800">
          <BlockStack align="center">
            <Spinner size="large" />
            <Text as="p">Loading product...</Text>
          </BlockStack>
        </Box>
      </Page>
    );
  }

  if (error || !product) {
    return (
      <Page title="Error">
        <Banner tone="critical">{error || 'Product not found'}</Banner>
        <Box padding="400">
          <Button onClick={() => navigate('/categories')}>Browse Products</Button>
        </Box>
      </Page>
    );
  }

  const pricingRows = product.pricing.map((p) => [p.quantityTier, `Rs.${p.unitPrice}`]);

  return (
    <Page
      title={product.name}
      backAction={{
        content: product.category.name,
        onAction: () => navigate(`/categories/${product.category.slug}`),
      }}
      titleMetadata={product.isHotSelling ? <Badge tone="success">Hot Selling</Badge> : undefined}
    >
      <Layout>
        {/* Product Images */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <div
                className="product-main-image"
                style={{
                  background: 'var(--p-color-bg-surface-secondary)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                {selectedImage ? (
                  <img
                    src={selectedImage}
                    alt={product.name}
                    loading="lazy"
                    width={400}
                    height={400}
                    style={{
                      width: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <Box
                    background="bg-surface-tertiary"
                    borderRadius="200"
                  />
                )}
              </div>

              {product.images.length > 1 && (
                <InlineStack gap="200">
                  {product.images.map((image) => (
                    <Box
                      key={image.id}
                      padding="100"
                      borderWidth="025"
                      borderColor={selectedImage === image.imageUrl ? 'border-success' : 'border'}
                      borderRadius="200"
                      background={selectedImage === image.imageUrl ? 'bg-surface-success' : 'bg-surface'}
                    >
                      <button
                        onClick={() => setSelectedImage(image.imageUrl)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        <img
                          src={image.imageUrl}
                          alt={`${product.name} - ${image.colorVariant || 'Image'}`}
                          loading="lazy"
                          width={60}
                          height={60}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                          }}
                        />
                      </button>
                    </Box>
                  ))}
                </InlineStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Product Details */}
        <Layout.Section variant="oneHalf">
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="400">
                <Text as="h1" variant="headingXl">
                  {product.name}
                </Text>

                <InlineStack gap="200">
                  <Text as="p" variant="bodySm" tone="subdued">
                    SKU: {product.sku}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    |
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Category: {product.category.name}
                  </Text>
                </InlineStack>

                {product.samplePrice && (
                  <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                    <InlineStack align="space-between">
                      <Text as="p" variant="bodyMd">
                        Sample Price
                      </Text>
                      <Text as="p" variant="headingLg" fontWeight="bold">
                        Rs.{product.samplePrice}
                      </Text>
                    </InlineStack>
                  </Box>
                )}

                <Divider />

                {product.description && (
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingMd">
                      Description
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {product.description}
                    </Text>
                  </BlockStack>
                )}
              </BlockStack>
            </Card>

            {/* Pricing Table */}
            {product.pricing.length > 0 && (
              <Card>
                <BlockStack gap="400">
                  <Text as="h3" variant="headingMd">
                    Bulk Pricing
                  </Text>
                  <DataTable
                    columnContentTypes={['text', 'numeric']}
                    headings={['Quantity', 'Price per Unit']}
                    rows={pricingRows}
                  />
                </BlockStack>
              </Card>
            )}

            {/* CTA */}
            <Card>
              <BlockStack gap="300">
                <Button variant="primary" size="large" fullWidth onClick={handleRequestQuote}>
                  Order Bulk
                </Button>
                <Button size="large" fullWidth onClick={handleRequestSample}>
                  Request Sample
                </Button>
                <Button
                  size="large"
                  fullWidth
                  onClick={handleToggleShortlist}
                  variant={product && isInShortlist(product.id) ? 'primary' : 'secondary'}
                  tone={product && isInShortlist(product.id) ? 'critical' : undefined}
                >
                  {product && isInShortlist(product.id) ? 'Remove from Shortlist' : 'Add to Shortlist'}
                </Button>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Related Products
                </Text>
                <InlineGrid columns={{ xs: 2, md: 4 }} gap="400">
                  {relatedProducts.map((related) => (
                    <Card key={related.id}>
                      <BlockStack gap="200">
                        <Box
                          minHeight="120px"
                          background="bg-surface-secondary"
                          borderRadius="200"
                        >
                          {related.primaryImage ? (
                            <img
                              src={related.primaryImage}
                              alt={related.name}
                              loading="lazy"
                              width={200}
                              height={120}
                              style={{
                                width: '100%',
                                height: '120px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                              }}
                            />
                          ) : (
                            <Box
                              minHeight="120px"
                              background="bg-surface-tertiary"
                              borderRadius="200"
                            />
                          )}
                        </Box>
                        <Text as="p" variant="bodySm" fontWeight="semibold">
                          {related.name}
                        </Text>
                        {related.startingPrice && (
                          <Text as="p" variant="bodySm">
                            From Rs.{related.startingPrice}
                          </Text>
                        )}
                        <Button
                          size="slim"
                          onClick={() => navigate(`/products/${related.slug}`)}
                        >
                          View
                        </Button>
                      </BlockStack>
                    </Card>
                  ))}
                </InlineGrid>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}
      </Layout>

      {/* Bulk Quantity Modal */}
      {product && (
        <BulkQuantityModal
          open={bulkModalOpen}
          onClose={() => setBulkModalOpen(false)}
          product={{
            id: product.id,
            name: product.name,
            slug: product.slug,
            primaryImage: product.images[0]?.imageUrl || null,
            pricing: product.pricing,
          }}
        />
      )}
    </Page>
  );
}
